import {
  Box,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import { getMirrorBaseUrl } from '../../utils/mirrorUrl';
import { getExplorerUrl } from '../../utils/explorerUrl';
import { getAuthAxiosInstance as authAxios } from '../../api/axios';
import { formatTokenBalance } from '../../utils/tokens';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import { ReactComponent as ExternalIcon } from 'assets/external-icon.svg';
import TokenIcon from 'components/TokenIcon';
import copyToClipboard from 'copy-to-clipboard';
import { useToast } from 'hooks/useToast';
import { useMediaQuery } from 'react-responsive';

interface MirrorNodeTokenInfo {
  name: string;
  symbol: string;
  token_id: string;
  type: string;
  decimals: string;
  total_supply: string;
  max_supply: string;
  supply_type: string;
  treasury_account_id: string;
  memo: string;
  created_timestamp: string;
}

const TokenDetailsModal = () => {
  const {
    modals: {
      [`${ModalType.TokenDetailsModal}`]: { open },
    },
    selectedToken,
    connectedNetwork,
    closeModal,
    setSelectedToken,
  } = useContext(GlobalContext);

  const { showToast } = useToast();
  const isScreenMedium = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });
  const [tokenInfo, setTokenInfo] = useState<MirrorNodeTokenInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const fetchTokenInfo = useCallback(async (tokenId: string) => {
    if (!connectedNetwork) return;

    setIsLoading(true);
    setFetchError('');

    try {
      const baseUrl = getMirrorBaseUrl(connectedNetwork);
      const isCustom = connectedNetwork?.startsWith('custom-');
      let data: any;

      if (isCustom) {
        const client = authAxios();
        if (!client) throw new Error('Not authenticated');
        const res = await client.get(`${baseUrl}/api/v1/tokens/${tokenId}`);
        data = res.data;
      } else {
        const response = await fetch(`${baseUrl}/api/v1/tokens/${tokenId}`);
        if (!response.ok) {
          setFetchError('Failed to fetch token details');
          return;
        }
        data = await response.json();
      }

      setTokenInfo(data);
    } catch {
      setFetchError('Failed to fetch token details');
    } finally {
      setIsLoading(false);
    }
  }, [connectedNetwork]);

  useEffect(() => {
    if (open && selectedToken?.token_id) {
      fetchTokenInfo(selectedToken.token_id);
    }
    if (!open) {
      setTokenInfo(null);
      setFetchError('');
    }
  }, [open, selectedToken?.token_id, fetchTokenInfo]);

  const handleClose = () => {
    closeModal(ModalType.TokenDetailsModal);
    setSelectedToken(null);
  };

  if (!selectedToken) return null;

  const formatSupply = (supply: string, decimals: string): string => {
    const dec = parseInt(decimals, 10);
    if (dec === 0) return Number(supply).toLocaleString();
    const adjusted = Number(supply) / Math.pow(10, dec);
    return adjusted.toLocaleString(undefined, { maximumFractionDigits: Math.min(dec, 6) });
  };

  const formatTimestamp = (ts: string): string => {
    const seconds = parseFloat(ts);
    return new Date(seconds * 1000).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const explorerUrl = getExplorerUrl(connectedNetwork!, 'token', selectedToken.token_id);

  const tokenType = selectedToken.type === 'FUNGIBLE_COMMON' ? 'Fungible' : 'Non-Fungible (NFT)';

  return (
    <Modal onClose={handleClose} isOpen={open} isCentered>
      <ModalOverlay />
      <ModalContent
        maxWidth="520px"
        w="full"
        mx={isScreenMedium ? 4 : 0}
      >
        <ModalHeader pt={6} px={6} pb={0}>
          <Flex alignItems="center" flexDirection="column" gap={3}>
            <TokenIcon symbol={selectedToken.symbol} size={48} tokenId={selectedToken.token_id} />
            <Text size="2xl" variant="bodyBold">
              {selectedToken.name}
            </Text>
            <Text size="sm" color="blackAlpha.600" mt={-2}>
              {selectedToken.symbol}
            </Text>
          </Flex>
          <ModalCloseButton
            mt={2}
            mr={2}
            color="#565656"
            _hover={{ border: 'none' }}
            _focusVisible={{ outline: 'none' }}
          />
        </ModalHeader>
        <ModalBody pb={8} pt={4} px={6}>
          <Flex direction="column" gap={4}>
            {/* Token ID */}
            <Box p={4} bg="primary" borderRadius={8}>
              <Text size="xs" color="blackAlpha.600">Token ID</Text>
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Text size="sm" variant="bodyBold">
                  {selectedToken.token_id}
                </Text>
                <CopyIcon
                  cursor="pointer"
                  onClick={() => {
                    copyToClipboard(selectedToken.token_id);
                    showToast('Copied to clipboard', 'success', undefined, 'top');
                  }}
                />
                {explorerUrl && (
                  <Link href={explorerUrl} isExternal cursor="pointer">
                    <ExternalIcon />
                  </Link>
                )}
              </Box>
            </Box>

            {/* Balance, Type & Decimals */}
            <Flex gap={4}>
              <Box flex={1} p={4} bg="primary" borderRadius={8}>
                <Text size="xs" color="blackAlpha.600">Balance</Text>
                <Text size="sm" variant="bodyBold" mt={1}>
                  {formatTokenBalance(selectedToken)}
                </Text>
              </Box>
              <Box flex={1} p={4} bg="primary" borderRadius={8}>
                <Text size="xs" color="blackAlpha.600">Type</Text>
                <Text size="sm" variant="bodyBold" mt={1}>
                  {tokenType}
                </Text>
              </Box>
              <Box flex={1} p={4} bg="primary" borderRadius={8}>
                <Text size="xs" color="blackAlpha.600">Decimals</Text>
                <Text size="sm" variant="bodyBold" mt={1}>
                  {selectedToken.decimals}
                </Text>
              </Box>
            </Flex>

            {/* Mirror Node extended info */}
            {isLoading && (
              <Flex justify="center" py={4}>
                <Spinner size="sm" />
                <Text size="xs" color="blackAlpha.600" ml={2}>Loading token details...</Text>
              </Flex>
            )}

            {fetchError && (
              <Text size="xs" color="red.500" textAlign="center">
                {fetchError}
              </Text>
            )}

            {tokenInfo && (
              <>
                {/* Supply Info */}
                <Flex gap={4}>
                  <Box flex={1} p={4} bg="primary" borderRadius={8}>
                    <Text size="xs" color="blackAlpha.600">Total Supply</Text>
                    <Text size="sm" variant="bodyBold" mt={1}>
                      {formatSupply(tokenInfo.total_supply, tokenInfo.decimals)}
                    </Text>
                  </Box>
                  <Box flex={1} p={4} bg="primary" borderRadius={8}>
                    <Text size="xs" color="blackAlpha.600">Max Supply</Text>
                    <Text size="sm" variant="bodyBold" mt={1}>
                      {tokenInfo.supply_type === 'INFINITE'
                        ? 'Infinite'
                        : formatSupply(tokenInfo.max_supply, tokenInfo.decimals)
                      }
                    </Text>
                  </Box>
                </Flex>

                {/* Treasury & Created */}
                <Flex gap={4}>
                  <Box flex={1} p={4} bg="primary" borderRadius={8}>
                    <Text size="xs" color="blackAlpha.600">Treasury Account</Text>
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                      <Text size="sm" variant="bodyBold">
                        {tokenInfo.treasury_account_id}
                      </Text>
                      <CopyIcon
                        cursor="pointer"
                        onClick={() => {
                          copyToClipboard(tokenInfo.treasury_account_id);
                          showToast('Copied to clipboard', 'success', undefined, 'top');
                        }}
                      />
                    </Box>
                  </Box>
                  <Box flexShrink={0} p={4} bg="primary" borderRadius={8}>
                    <Text size="xs" color="blackAlpha.600">Created</Text>
                    <Text size="sm" variant="bodyBold" mt={1}>
                      {formatTimestamp(tokenInfo.created_timestamp)}
                    </Text>
                  </Box>
                </Flex>

                {/* Memo - only if present */}
                {tokenInfo.memo && (
                  <Box p={4} bg="primary" borderRadius={8}>
                    <Text size="xs" color="blackAlpha.600">Memo</Text>
                    <Text size="sm" variant="bodyBold" mt={1} wordBreak="break-word">
                      {tokenInfo.memo}
                    </Text>
                  </Box>
                )}
              </>
            )}

            {/* Explorer Link */}
            {explorerUrl && (<Flex justifyContent="center" mt={2}>
              <Link
                href={explorerUrl}
                isExternal
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                px={3}
                py={1.5}
                fontSize="xs"
                fontWeight="500"
                color="#2d8a45"
                bg="#f0faf2"
                borderRadius={6}
                _hover={{ bg: '#e5f7ea', color: '#1a6e32', textDecoration: 'none' }}
                transition="all 0.15s"
              >
                View in Explorer
              </Link>
            </Flex>)}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TokenDetailsModal;
